import React from 'react';
import ReactDOM from 'react-dom';
import { FiFileText, FiX } from 'react-icons/fi';
import '../../styles/FilePreviewModal.css';

interface FilePreviewModalProps {
  name: string;
  url: string;
  onClose: () => void;
}

// Portaled to <body> so it's centered over the real browser viewport instead
// of being boxed in by whatever (possibly narrow) container it's opened from.
// Matches the look of Command Center's own file-preview modal so previews are
// visually consistent across chat and Command Center.
const FilePreviewModal: React.FC<FilePreviewModalProps> = ({ name, url, onClose }) => {
  const isImage = /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name);
  const isPdf = /\.pdf$/i.test(name);
  const isDocx = /\.docx?$/i.test(name);
  const isMarkdown = /\.md$/i.test(name);
  const isText = /\.(txt|log|csv)$/i.test(name);
  
  return ReactDOM.createPortal(
    <div className="fpm-backdrop" onClick={onClose}>
      <div className="fpm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fpm-header">
          <span className="fpm-icon-badge">
            <FiFileText size={15} />
          </span>
          <span className="fpm-name" title={name}>{name}</span>
          <button type="button" className="fpm-close" onClick={onClose} title="Close preview">
            <FiX size={16} />
          </button>
        </div>
        <div className="fpm-body">
          {isImage ? (
            <img src={url} alt={name} className="fpm-img" />
          ) : isPdf ? (
            // Native toolbar left in place (not hidden via #toolbar=0) — its own
            // Download button reliably saves the file; an <a download> click just
            // navigates to it in a new tab instead of saving, since that's only
            // guaranteed for same-origin URLs and this one isn't. #navpanes=0 only
            // closes the page-thumbnails sidebar by default — it doesn't touch the
            // toolbar itself, so Download stays visible and working.
            <iframe
              src={`${url}#navpanes=0`}
              title={name}
              className="fpm-frame"
            />
          ) : isDocx ? (
            // Microsoft Office Online Web Viewer for DOCX/DOC files
            // Note: The URL must be a direct file URL, not an HTML preview
            <DocxViewer url={url} fileName={name} />
          ) : isMarkdown || isText ? (
            // Fetch and display text content
            <TextFileViewer url={url} fileName={name} />
          ) : (
            // Fallback: generic iframe or download prompt
            <div className="fpm-unsupported">
              <div className="fpm-unsupported-icon">📄</div>
              <div className="fpm-unsupported-text">
                <p><strong>{name}</strong></p>
                <p>This file type cannot be previewed in browser.</p>
              </div>
              <a href={url} download={name} className="fpm-download-btn" target="_blank" rel="noreferrer">
                Download File
              </a>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// Helper component for DOCX file viewer with HTML detection
const DocxViewer: React.FC<{ url: string; fileName: string }> = ({ url, fileName }) => {
  const [isHtmlContent, setIsHtmlContent] = React.useState(false);
  const [checking, setChecking] = React.useState(true);

  React.useEffect(() => {
    // Check if the URL returns HTML instead of a proper file
    const checkContentType = async () => {
      try {
        const res = await fetch(url, { method: 'HEAD' });
        const contentType = res.headers.get('content-type') || '';
        
        // If content is HTML, it means the server is returning HTML preview instead of the file
        if (contentType.includes('text/html')) {
          setIsHtmlContent(true);
        }
      } catch (err) {
        // Network error - try to load anyway
        console.warn('Could not check file content type:', err);
      } finally {
        setChecking(false);
      }
    };

    checkContentType();
  }, [url]);

  if (checking) {
    return <div className="fpm-text-loading">Checking file...</div>;
  }

  if (isHtmlContent) {
    return (
      <div className="fpm-unsupported">
        <div className="fpm-unsupported-icon">⚠️</div>
        <div className="fpm-unsupported-text">
          <p><strong>{fileName}</strong></p>
          <p>File is being served as HTML. Please download to view properly.</p>
        </div>
        <a href={url} download={fileName} className="fpm-download-btn" target="_blank" rel="noreferrer">
          Download DOCX File
        </a>
      </div>
    );
  }

  return (
    <iframe
      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
      title={fileName}
      className="fpm-frame"
      allow="fullscreen"
    />
  );
};

// Helper component to display text file content
const TextFileViewer: React.FC<{ url: string; fileName: string }> = ({ url, fileName }) => {
  const [content, setContent] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to load file');
        const text = await res.text();
        setContent(text);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error loading file');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [url]);

  if (loading) return <div className="fpm-text-loading">Loading...</div>;
  if (error) return <div className="fpm-text-error">{error}</div>;

  return (
    <pre className="fpm-text-content">
      <code>{content}</code>
    </pre>
  );
};

export default FilePreviewModal;
